import { createAsyncThunk } from "@reduxjs/toolkit";
import Axios from "axios";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import { setIsSpinnerVisible } from "store/modules/AppConfig/index.store";

import CatalogApi from "services/CatalogApi";
import { getFirstApiErrorMessage } from "utils/apiError";

import i18next from "i18next";
import {
  CoverageAreasResponse,
  UpdateWorkingAreaResponse,
  UpdateWorkingAreaRequest,
  CoverageAreasRequest,
  CoverageCatalogProductsResponse,
} from "./types.store";

export const handleGetCatalogAreaRequest = createAsyncThunk<
  CoverageAreasResponse,
  CoverageAreasRequest
>("getCatalogAreas", async (data, { rejectWithValue, dispatch }) => {
  try {
    dispatch(setIsSpinnerVisible(true));

    const response = await CatalogApi.get(
      `/v1/dealers/${data.dealerId}/coverage-areas/${data.coverageAreaId}`,
    );

    return response.data;
  } catch (err) {
    toast(i18next.t("failedLoadProductPleaseAgain").toString(), {
      type: "error",
      autoClose: false,
    });

    if (Axios.isAxiosError(err) && err && err.response) {
      return rejectWithValue(err.response.data);
    }
    return rejectWithValue(err);
  } finally {
    dispatch(setIsSpinnerVisible(false));
  }
});

export const handleUpdateCatalogAreaRequest = createAsyncThunk<
  UpdateWorkingAreaResponse,
  { data: UpdateWorkingAreaRequest[]; id: string; dealerId: string }
>("putCatalogArea", async (request, { rejectWithValue, dispatch }) => {
  try {
    dispatch(setIsSpinnerVisible(true));

    const response = await CatalogApi.put(
      `/v1/dealers/${request.dealerId}/coverage-areas/${request.id}`,
      { productPrices: request.data },
    );

    const responseErrorMessage = getFirstApiErrorMessage(response.data);
    if (responseErrorMessage) {
      return rejectWithValue(response.data);
    }

    await dispatch(
      handleGetCatalogAreaRequest({
        coverageAreaId: request.id,
        dealerId: request.dealerId,
      }),
    ).unwrap();

    return response.data;
  } catch (err) {
    if (Axios.isAxiosError(err) && err.response?.data) {
      return rejectWithValue(err.response.data);
    }
    return rejectWithValue(err);
  } finally {
    dispatch(setIsSpinnerVisible(false));
  }
});

export const handleGetCatalogProductsRequest =
  createAsyncThunk<CoverageCatalogProductsResponse>(
    "getCatalogProducts",
    async (_, { rejectWithValue, dispatch }) => {
      try {
        dispatch(setIsSpinnerVisible(true));
        const response = await CatalogApi.get(`/v1/products`);

        return response.data;
      } catch (err) {
        toast(i18next.t("failedLoadProductCatalogPleaseAgain").toString(), {
          type: "error",
          autoClose: false,
        });

        if (Axios.isAxiosError(err) && err && err.response) {
          return rejectWithValue(err.response.data);
        }
        return rejectWithValue(err);
      } finally {
        dispatch(setIsSpinnerVisible(false));
      }
    },
  );
